import React, { useState, useCallback, useEffect } from "react";
import JsBarcode from "jsbarcode";
import parse from "html-react-parser";
import {
  BarcodeImageContainer,
  BarcodeText,
  BottomTextContainer,
  CenteredBoldText,
  CenteredText,
  SuccessTopContainer,
  HelpIcon,
  BarcodeContentContainer,
  LegalTextContainer,
} from "../styles";
import HintCard from "../../../../common/components/HintCard";
import { delay, fromPublic, sendAnalytics, wireCallbacks } from "../../../../utils";
import { CircularProgress } from "@mui/material";


interface SuccessStateProps {
  barcode: string;
  topText: string;
  bottomText: string;
  legal?: string;
  hintCard: {
    title: string;
    image: string;
    content: string;
    buttonText: string;
    icon: string;
  };
  onBarcodeRendered?: () => void;
}

const SuccessState: React.FC<SuccessStateProps> = ({
  barcode,
  topText,
  bottomText,
  legal,
  hintCard,
  onBarcodeRendered,
}) => {
  const [showHint, setShowHint] = useState(false);
  const [isBarcodeRendered, setIsBarcodeRendered] = useState(false);

  const openHint = async () => {
    setShowHint(true);
    try {
      await delay(100);
      await sendAnalytics("barcodeHintCardOpen");
      await delay(200);
    } catch (error) {
      console.warn("Analytics failed for hint card open:", error);
    }
  };

  const closeHint = () => {
    setShowHint(false);
  };

  const barcodeRef = useCallback(
    (node: HTMLImageElement | null) => {
      if (node && barcode) {
        try {
          JsBarcode(node, barcode, {
            displayValue: false,
            width: 2.5,
            height: 80,
            margin: 0,
          });
          setIsBarcodeRendered(true);
        } catch (e) {
          console.error("Failed to generate barcode:", e);
        }
      }
    },
    [barcode]
  );

  // Notify parent when barcode is rendered
  useEffect(() => {
    if (isBarcodeRendered && onBarcodeRendered) {
      setTimeout(() => {
        onBarcodeRendered();
      }, 100);
    }
  }, [isBarcodeRendered, onBarcodeRendered]);

  // Wire up custom callbacks for any links rendered within the legal text.
  useEffect(() => {
    if (legal) {
      try {
        wireCallbacks();
      } catch (err) {
        console.warn("Failed to wire callbacks for legal links:", err);
      }
    }
  }, [legal]);

  const formattedBarcode = barcode.replace(/(.{4})/g, "$1 ").trim();

  const formatBottomText = (text: string) => {
    const parts = text.split(/(\d+)/g);
    return parts.map((part, index) =>
      /\d+/.test(part) ? <strong key={`${part}-${index}`}>{part}</strong> : part
    );
  };

  return (
    <>
      <SuccessTopContainer>
        <CenteredBoldText>{topText}</CenteredBoldText>
        <HelpIcon src={fromPublic(hintCard.icon)} onClick={openHint} />
      </SuccessTopContainer>
      <BarcodeContentContainer>
        <BarcodeImageContainer style={{ position: "relative" }}>
          {!isBarcodeRendered && (
            <div
              style={{
                position: "absolute",
                top: 0,
                bottom: 0,
                left: 0,
                right: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "rgba(255,255,255,0.8)",
                zIndex: 2,
              }}
            >
              <CircularProgress sx={{ color: "#0e893c" }} />
            </div>
          )}
          <img ref={barcodeRef} alt="Barcode" />
        </BarcodeImageContainer>
        <BarcodeText>{formattedBarcode}</BarcodeText>
      </BarcodeContentContainer>
      <BottomTextContainer>
        <CenteredText>{formatBottomText(bottomText)}</CenteredText>
      </BottomTextContainer>
      {legal && <LegalTextContainer>{parse(legal)}</LegalTextContainer>}
      <HintCard
        show={showHint}
        onClose={closeHint}
        title={hintCard.title}
        image={hintCard.image}
        content={hintCard.content}
        buttonText={hintCard.buttonText}
      />
    </>
  );
};

export default SuccessState;
